import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { LANGS } from "@/i18n/translations";
import { CONTACT } from "@/i18n/translations";
import { Logo } from "@/components/Logo";
import { scrollToId } from "@/hooks/useLenis";
import { MessageCircle, Menu, X, Phone } from "lucide-react";

const Sections = ["services", "partners", "contact"];

export const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 left-0 right-0 z-50"
      data-testid="navbar"
    >
      <div
        className="mx-auto max-w-[1400px] px-5 sm:px-8 backdrop-blur-xl transition-[background,box-shadow,border-color] duration-300"
        style={{
          background: scrolled ? "rgba(255,255,255,0.75)" : "rgba(255,255,255,0.45)",
          borderBottom: scrolled ? "1px solid rgba(9,10,12,0.07)" : "1px solid transparent",
        }}
      >
        <div className="flex items-center justify-between h-[70px]">
          <button onClick={() => go("hero")} data-testid="nav-logo-btn" aria-label="Home">
            <Logo />
          </button>

          <nav className="hidden md:flex items-center gap-9">
            {Sections.map((s) => (
              <button
                key={s}
                onClick={() => go(s)}
                data-testid={`nav-${s}`}
                className="pt-link-underline text-sm font-medium text-[#4B5563] hover:text-[#090A0C] transition-colors"
              >
                {t.nav[s]}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center rounded-full border border-[#E5E7EB] bg-white p-0.5" data-testid="lang-switcher">
              {LANGS.map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  data-testid={`lang-${l}`}
                  className="relative px-2.5 py-1 text-xs font-semibold uppercase rounded-full transition-colors"
                  style={{ color: lang === l ? "#fff" : "#4B5563" }}
                >
                  {lang === l && (
                    <motion.span
                      layoutId="lang-pill"
                      className="absolute inset-0 rounded-full"
                      style={{ background: "var(--pt-blue)" }}
                      transition={{ type: "spring", stiffness: 400, damping: 32 }}
                    />
                  )}
                  <span className="relative z-10">{l}</span>
                </button>
              ))}
            </div>

            <a
              href={`tel:${CONTACT.phoneRaw}`}
              data-testid="nav-phone"
              className="hidden lg:flex flex-col items-end leading-tight mr-1 group"
            >
              <span className="text-[10px] uppercase tracking-wider text-[#4B5563]">Otto</span>
              <span className="flex items-center gap-1.5 text-sm font-bold text-[#090A0C] transition-colors group-hover:text-[#0B87B0]">
                <Phone size={14} color="var(--pt-blue)" /> {CONTACT.phone}
              </span>
            </a>

            <a
              href={`https://wa.me/${CONTACT.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp"
              className="hidden sm:inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white transition-transform hover:scale-[1.03]"
              style={{ background: "var(--pt-orange)" }}
            >
              <MessageCircle size={16} /> {t.nav.cta}
            </a>

            <button className="md:hidden p-2 text-[#090A0C]" onClick={() => setOpen((v) => !v)} data-testid="nav-mobile-toggle" aria-label="Menu">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden overflow-hidden bg-white border-b border-[#E5E7EB]"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {Sections.map((s) => (
                <button key={s} onClick={() => go(s)} data-testid={`mnav-${s}`} className="text-left text-lg font-display font-semibold text-[#090A0C]">
                  {t.nav[s]}
                </button>
              ))}
              <div className="flex items-center gap-2 pt-2">
                {LANGS.map((l) => (
                  <button
                    key={l}
                    onClick={() => setLang(l)}
                    data-testid={`mlang-${l}`}
                    className="px-3 py-1.5 text-xs font-semibold uppercase rounded-full border border-[#E5E7EB]"
                    style={{ background: lang === l ? "var(--pt-blue)" : "transparent", color: lang === l ? "#fff" : "#4B5563" }}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-white" style={{ background: "var(--pt-orange)" }}>
                <MessageCircle size={16} /> {t.nav.cta}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
